const alphaNumericWithSpecialChars =
  'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*()_+~`|}{[]:;?><,./-=';

export function generateSqlPassword(length: number): string {
  let password = '';
  const cryptoArray = new Uint32Array(length);
  globalThis.crypto.getRandomValues(cryptoArray);

  for (let i = 0; i < length; i++) {
    const randomIndex = cryptoArray[i] % alphaNumericWithSpecialChars.length;
    password += alphaNumericWithSpecialChars[randomIndex];
  }

  return password;
}

export function generateAlphaNumericPassword(length: number): string {
  const lowerCase = 'abcdefghijklmnopqrstuvwxyz';
  const upperCase = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  const digits = '0123456789';
  const alphaNumeric = lowerCase + upperCase + digits;

  let password = '';
  const cryptoArray = new Uint32Array(length);
  globalThis.crypto.getRandomValues(cryptoArray);

  // Ensure at least one lowercase, one uppercase, and one digit
  password += lowerCase[cryptoArray[0] % lowerCase.length];
  password += upperCase[cryptoArray[1] % upperCase.length];
  password += digits[cryptoArray[2] % digits.length];

  for (let i = 3; i < length; i++) {
    const randomIndex = cryptoArray[i] % alphaNumeric.length;
    password += alphaNumeric[randomIndex];
  }

  // Shuffle the password to ensure randomness
  password = password
    .split('')
    .sort(() => Math.random() - 0.5)
    .join('');

  return password;
}

/**
 * Currency configuration for the dashboard.
 */
export interface CurrencyConfig {
  code: 'USD' | 'NOK';
  symbol: string;
  rate: number; // Conversion factor FROM the base data (e.g. if base is USD, rate=10.5 for NOK)
  label: string;
}

export const USD: CurrencyConfig = {
  code: 'USD',
  symbol: '$',
  rate: 1,
  label: 'USD',
};
export const NOK: CurrencyConfig = {
  code: 'NOK',
  symbol: 'KR',
  rate: 10.5,
  label: 'NOK',
};

/**
 * Converts a value from baseCurrency to the target currency (cfg).
 */
export const convertCurrency = (
  n: number,
  cfg: CurrencyConfig,
  baseCurrency: string,
): number => {
  if (cfg.code === baseCurrency) return n;
  if (baseCurrency === 'NOK' && cfg.code === 'USD') return n / 10.5;
  if (baseCurrency === 'USD' && cfg.code === 'NOK') return n * 10.5;
  return n;
};

/**
 * Compact formatter: $6.8k or KR 71.4k
 */
export const fmtCompact = (
  n: number,
  cfg: CurrencyConfig = NOK,
  baseCurrency: string = 'NOK',
): string => {
  const val = convertCurrency(n, cfg, baseCurrency);

  const s = cfg.symbol === 'KR' ? 'KR ' : cfg.symbol;
  return val >= 1000
    ? `${s}${(val / 1000).toFixed(1)}k`
    : `${s}${val.toFixed(0)}`;
};

/**
 * Full precision formatter: $6,820.44 or KR 71,614.62
 */
export const fmtFull = (
  n: number,
  cfg: CurrencyConfig = NOK,
  baseCurrency: string = 'NOK',
): string => {
  const val = convertCurrency(n, cfg, baseCurrency);

  const s = cfg.symbol === 'KR' ? 'KR ' : cfg.symbol;
  const locale = cfg.code === 'USD' ? 'en-US' : 'en-NO';
  return `${s}${val.toLocaleString(locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

/**
 * Percentage change between current and previous value.
 */
export const pctDelta = (current: number, previous: number): number =>
  previous === 0 ? 0 : ((current - previous) / previous) * 100;

/**
 * Extracts the leaf resource type from an Azure resource type string.
 * e.g. "microsoft.compute/virtualmachines" → "virtualmachines"
 */
export const leafType = (type: string): string =>
  type.split('/').at(-1) ?? type;
